//#region header */
/**************************************************************************************************
//
//  Description: Organisation page which displays the list of organisations and the details of the selected organisation.
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useEffect, useRef, useState } from "react";

import useOrganisationQueryStore from "../context/OrganisationStore";
import useOrganisations from "./../hooks/useOrganisations";
import usePostOrganisations from "../hooks/usePostOrganisations";
import { validateOrganisationData } from "../utils/OrganisationValidation";

import { OrganisationErrorType } from "../models/organisationErrorType";
import OrganisationGridType from "../models/organisationGridType";

import { Backdrop, Box, Button, CircularProgress, Grid2, Stack } from "@mui/material";

import OrganisationTree from "../forms/OrganisationTree";
import OrganisationForm from "../forms/OrganisationForm";

import SaveIcon from "@mui/icons-material/Save";
import ErrorIcon from "@mui/icons-material/Error";

import { dataFormStyle, getSaveButtonStyle, toolbarStyle } from "../utils/ADSStyles";

interface OrganisationPageProps {}

const OrganisationPage: React.FC<OrganisationPageProps> = () => {
  const { data, error, isLoading } = useOrganisations();

  const organisationNames = useOrganisationQueryStore((s) => s.organisationQuery.organisationNames);
  const validationErrors = useOrganisationQueryStore((s) => s.organisationQuery.validationErrors);
  const setOrganisationNames = useOrganisationQueryStore((s) => s.setOrganisationNames);
  const setOrganisationChanged = useOrganisationQueryStore((s) => s.setOrganisationChanged);

  const postOrganisations = usePostOrganisations((newData: string[]) => {
    setOrganisationData(
      newData
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
        .map((x, index) => {
          return { id: index, organisation: x };
        })
    );
    setOrganisationNames(newData);
    setOrganisationChanged(false);
    setEnableSave(false);
    if (selectedItemId !== newSelectedItemId.current) handleItemSelectedEvent(newSelectedItemId.current);
  });

  const [organisationData, setOrganisationData] = useState<OrganisationGridType[]>([]);

  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [organisationError, setOrganisationError] = useState<OrganisationErrorType[] | undefined>(undefined);
  const [selectedItemId, setSelectedItemId] = useState<number | undefined>(undefined);
  const [expanded, setExpanded] = useState<string[]>([]);

  const [haveErrors, setHaveErrors] = useState<boolean>(false);
  const [errorIds, setErrorIds] = useState<number[]>([]);
  const [enableSave, setEnableSave] = useState<boolean>(false);

  const [organisationFormData, setOrganisationFormData] = useState<OrganisationGridType | undefined>();

  const newSelectedItemId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (error) setErrorText(error.message);
  }, [error]);

  /**
   * Method to handle the event when the user chooses to save the changes made to the current application.
   */
  const handleSaveClicked = () => {
    if (!organisationData || organisationData.length === 0 || (organisationError && organisationError.length > 0))
      return;
    postOrganisations.mutate(organisationData.map((x) => x.organisation));
  };

  /**
   * Event to handle when a node from the tree view is selected.
   *
   * @param nodeId The id of the selected node.
   */
  const handleItemSelectedEvent = (nodeId: number | undefined): void => {
    newSelectedItemId.current = nodeId;
    setSelectedItemId(nodeId);
    const selectedOrganisation = organisationData.find((org) => org.id === nodeId);
    setOrganisationFormData(selectedOrganisation);
  };

  /**
   * Method to add new organisation.
   */
  const handleAddOrganisation = (): void => {
    const newOrganisation: OrganisationGridType = { id: organisationData.length, organisation: "_New Organisation" };
    setOrganisationData((prevState) => [...prevState, newOrganisation]);
    setOrganisationNames([...(organisationNames || []), newOrganisation.organisation]);
    newSelectedItemId.current = newOrganisation.id;
    setSelectedItemId(newOrganisation.id);
    setOrganisationFormData(newOrganisation);
    setOrganisationChanged(true);
    setEnableSave(true);
  };

  /**
   * Method to handle the event when the expanded items are changed.
   *
   * @param items The expanded items.
   */
  const handleExpandedItemsChange = (items: string[]): void => {
    setExpanded(items);
  };

  /**
   * Method to handle the data change from the organisation form.
   *
   * @param newData The new organisation data.
   */
  const handleDataChange = (newData: OrganisationGridType): void => {
    if (!newData) return;

    if (newSelectedItemId.current && organisationData.find((org) => org.id === newSelectedItemId.current)) {
      // Changed existing organisation
      setOrganisationData((prevState) =>
        prevState.map((org) =>
          org.id === newSelectedItemId.current ? { ...org, organisation: newData.organisation } : org
        )
      );
      const updatedNames = (organisationNames || []).map((org) =>
        org === organisationData.find((a) => a.id === newSelectedItemId.current)?.organisation
          ? newData.organisation
          : org
      );
      setOrganisationNames(updatedNames);
      newSelectedItemId.current = newData.id;
    } else {
      // New organisation
      setOrganisationData((prevState) => [...prevState, { id: prevState.length, organisation: newData.organisation }]);
      setOrganisationNames([...(organisationNames || []), newData.organisation]);
      newSelectedItemId.current = newData.id;
    }
    setOrganisationFormData(newData);
    setOrganisationChanged(true);
    setEnableSave(true);
  };

  useEffect(() => {
    if (!isLoading && (!organisationData || organisationData.length === 0)) {
      if (data && data.length > 0) {
        setOrganisationData(
          data
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
            .map((x, index) => {
              return { id: index, organisation: x };
            })
        );
        setOrganisationNames(data);
        setOrganisationChanged(false);
        setEnableSave(false);
        setOrganisationError(undefined);
      }
    }
  }, [data, organisationData, isLoading, setOrganisationChanged, setOrganisationNames]);

  useEffect(() => {
    setHaveErrors(!!errorText || (organisationError?.length ?? 0) > 0);

    if (organisationError && organisationError.length > 0) {
      const ids: number[] = organisationError.map((x) => x.id);
      setErrorIds(ids);
    }
  }, [errorText, organisationError]);

  useEffect(() => {
    if (organisationData && organisationData.length > 0) {
      const errors = validateOrganisationData(organisationData);
      setOrganisationError(errors);
    }
  }, [organisationData]);

  useEffect(() => {
    setOrganisationError(validationErrors);
  }, [validationErrors]);

  return isLoading ? (
    <div>
      <Backdrop open={isLoading}>
        Loading data, please wait...
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  ) : (
    <>
      <Box sx={dataFormStyle("ADSOrganisationsPageControl")}>
        <Grid2 container justifyContent="flex-start" spacing={0}>
          <Grid2 size={3}>
            <OrganisationTree
              data={organisationData}
              selectedItem={selectedItemId}
              expanded={expanded}
              haveErrors={haveErrors}
              errorIds={errorIds}
              onItemSelected={handleItemSelectedEvent}
              onAddOrganisation={handleAddOrganisation}
              onExpandedItemsChange={handleExpandedItemsChange}
            />
          </Grid2>
          <Grid2 size={9}>
            <Box sx={toolbarStyle}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
                sx={{ ml: "12px", mt: "8px" }}
              >
                <Button
                  id="organisation-save-button"
                  sx={getSaveButtonStyle(haveErrors)}
                  variant="text"
                  startIcon={!haveErrors ? <SaveIcon /> : <ErrorIcon />}
                  disabled={!enableSave}
                  onClick={handleSaveClicked}
                >
                  Save
                </Button>
              </Stack>
            </Box>
            {organisationFormData && (
              <OrganisationForm
                data={organisationFormData}
                errors={organisationError}
                onDataChange={handleDataChange}
              />
            )}
          </Grid2>
        </Grid2>
      </Box>
    </>
  );
};

export default OrganisationPage;
